import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { OutboundSubscriptionView, OutboundSubscriptionWrite } from '../api/contracts';
import { useMonitorApi } from '../app/api-context';
import { useCan } from '../auth/auth-context';
import { ErrorBanner } from '../components/ErrorBanner';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { useAsyncResource } from '../hooks/use-async-resource';
import { formatDateTime } from '../lib/format';
import { SYSTEM_OPTIONS } from '../lib/catalogs';

function SubscriptionForm({
  subscription,
  onClose,
  onSaved,
}: {
  subscription: OutboundSubscriptionView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useMonitorApi();
  const [eventType, setEventType] = useState(subscription?.eventType ?? '');
  const [system, setSystem] = useState<OutboundSubscriptionWrite['system']>(
    subscription?.system ?? 'n8n',
  );
  const [targetUrl, setTargetUrl] = useState(subscription?.targetUrl ?? '');
  const [secret, setSecret] = useState('');
  const [active, setActive] = useState(subscription?.active ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const apiKey = eventType === 'crm.customer.snapshot' && system === 'decorapos';
  function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    void api
      .saveSubscription(subscription?.id ?? null, {
        eventType,
        system,
        targetUrl,
        active,
        ...(secret === '' ? {} : { secret }),
      })
      .then(onSaved, (err: unknown) => {
        setError(err);
        setBusy(false);
      });
  }
  return (
    <section className="panel" aria-labelledby="subscription-form-title">
      <div className="panel__body">
        <h2 id="subscription-form-title">
          {subscription ? 'Editar suscripción' : 'Nueva suscripción'}
        </h2>
        <form onSubmit={submit}>
          <fieldset disabled={busy} style={{ border: 0, padding: 0 }}>
            <div className="filters">
              <label className="field">
                <span className="field__label">Evento</span>
                <input
                  className="input"
                  required
                  maxLength={128}
                  value={eventType}
                  disabled={subscription !== null}
                  onChange={(e) => setEventType(e.target.value)}
                  placeholder="order.status.changed"
                />
              </label>
              <label className="field">
                <span className="field__label">Sistema destino</span>
                <select
                  className="input"
                  value={system}
                  disabled={subscription !== null}
                  onChange={(e) => setSystem(e.target.value as OutboundSubscriptionWrite['system'])}
                >
                  {SYSTEM_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field field--wide">
                <span className="field__label">URL de destino</span>
                <input
                  className="input"
                  type="url"
                  required
                  maxLength={2048}
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  placeholder="https://…/webhooks/pedidos"
                />
              </label>
              <label className="field">
                <span className="field__label">{apiKey ? 'API key' : 'Secreto HMAC'}</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  maxLength={4096}
                  value={secret}
                  required={active && !subscription?.hasSecret}
                  onChange={(e) => setSecret(e.target.value)}
                />
              </label>
            </div>
            <p className="muted">
              {subscription?.hasSecret
                ? 'Deja el secreto vacío para conservarlo.'
                : 'Configura el secreto antes de activar.'}{' '}
              {apiKey
                ? 'Clientes usa x-amoble-api-key y la ruta /webhooks/clientes.'
                : 'Los eventos se firman con HMAC.'}
            </p>
            <label>
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />{' '}
              Suscripción activa
            </label>
            <p className="muted">
              El evento debe coincidir exactamente con uno publicado por el middleware. Cambiar URL,
              secreto o estado también afecta las entregas pendientes. Desactivar conserva el
              historial; reactivar no reenvía eventos anteriores.
            </p>
            <ErrorBanner error={error} />
            <div className="row">
              <button className="button button--primary" type="submit">
                {busy ? 'Guardando…' : 'Guardar suscripción'}
              </button>
              <button className="button" type="button" onClick={onClose}>
                Cancelar
              </button>
            </div>
          </fieldset>
        </form>
      </div>
    </section>
  );
}

function DeliveryHistory({
  subscription,
  onClose,
}: {
  subscription: OutboundSubscriptionView;
  onClose: () => void;
}) {
  const api = useMonitorApi();
  const [page, setPage] = useState(1);
  const resource = useAsyncResource(
    () => api.listSubscriptionDeliveries(subscription.id, page),
    [api, subscription.id, page],
  );
  return (
    <section className="panel">
      <header className="panel__header">
        <h2>Entregas · {subscription.eventType}</h2>
        <button className="button" onClick={onClose}>
          Cerrar historial
        </button>
      </header>
      <ErrorBanner error={resource.error} onRetry={resource.reload} />
      {resource.loading ? (
        <LoadingBlock />
      ) : resource.data?.items.length === 0 ? (
        <EmptyState title="Sin entregas registradas." />
      ) : (
        <div className="table__wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Estado</th>
                <th>Intentos</th>
                <th>Error</th>
                <th>Mensaje</th>
              </tr>
            </thead>
            <tbody>
              {resource.data?.items.map((d) => (
                <tr key={d.id}>
                  <td>{formatDateTime(d.createdAt)}</td>
                  <td>
                    {d.status === 'delivered'
                      ? 'Entregada'
                      : d.status === 'failed'
                        ? 'Fallida'
                        : 'Pendiente'}
                  </td>
                  <td>{d.attempts}</td>
                  <td>{d.lastError ?? '—'}</td>
                  <td>
                    <Link to={`/mensajes/${d.messageId}`}>Ver detalle</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="panel__body row">
        <button
          className="button"
          disabled={page === 1 || resource.loading}
          onClick={() => setPage(page - 1)}
        >
          Anterior
        </button>
        <span>Página {page}</span>
        <button
          className="button"
          disabled={!resource.data?.hasMore || resource.loading}
          onClick={() => setPage(page + 1)}
        >
          Siguiente
        </button>
        <button className="button" onClick={resource.reload}>
          Actualizar entregas
        </button>
      </div>
    </section>
  );
}

export function OutboundSubscriptionsPage() {
  const api = useMonitorApi();
  const canWrite = useCan('subscriptions:write');
  const resource = useAsyncResource(() => api.listSubscriptions(), [api]);
  const [search, setSearch] = useState('');
  const [state, setState] = useState('all');
  const [system, setSystem] = useState('');
  const [editing, setEditing] = useState<OutboundSubscriptionView | null | undefined>(undefined);
  const [history, setHistory] = useState<OutboundSubscriptionView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const rows = (resource.data ?? []).filter(
    (s) =>
      (!system || s.system === system) &&
      (state === 'all' || s.active === (state === 'active')) &&
      `${s.eventType} ${s.targetUrl}`.toLowerCase().includes(search.toLowerCase()),
  );
  function toggle(s: OutboundSubscriptionView) {
    setBusy(true);
    setError(null);
    setNotice('');
    void api.setSubscriptionActive(s.id, !s.active).then(
      () => {
        setBusy(false);
        setNotice(
          s.active ? 'Suscripción desactivada. Se conserva su historial.' : 'Suscripción activada.',
        );
        resource.reload();
      },
      (err: unknown) => {
        setBusy(false);
        setError(err);
      },
    );
  }
  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Webhooks salientes</h1>
          <p className="page__subtitle">
            Destinos que reciben los eventos del middleware. Desactivar conserva las suscripciones y
            sus entregas.
          </p>
        </div>
        <div className="row">
          <Link className="button" to="/referencia/webhooks-salientes">
            Guía de uso
          </Link>
          <button className="button" onClick={resource.reload}>
            Actualizar
          </button>
          {canWrite && (
            <button
              className="button button--primary"
              disabled={busy || editing !== undefined}
              onClick={() => {
                setEditing(null);
                setNotice('');
              }}
            >
              Nueva suscripción
            </button>
          )}
        </div>
      </header>
      {notice && (
        <div className="banner banner--info" role="status">
          {notice}
        </div>
      )}
      <ErrorBanner error={resource.error} onRetry={resource.reload} />
      <ErrorBanner error={error} />
      {canWrite && editing !== undefined && (
        <SubscriptionForm
          key={editing?.id ?? 'new'}
          subscription={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            setNotice('Suscripción guardada.');
            resource.reload();
          }}
        />
      )}
      <section className="panel">
        <div className="panel__body filters">
          <label className="field">
            <span className="field__label">Buscar evento o URL</span>
            <input
              className="input"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">Filtrar sistema</span>
            <select className="input" value={system} onChange={(e) => setSystem(e.target.value)}>
              <option value="">Todos</option>
              {SYSTEM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Estado</span>
            <select className="input" value={state} onChange={(e) => setState(e.target.value)}>
              <option value="all">Todas</option>
              <option value="active">Activas</option>
              <option value="inactive">Inactivas</option>
            </select>
          </label>
        </div>
        {resource.loading && !resource.data ? (
          <LoadingBlock />
        ) : rows.length === 0 ? (
          <EmptyState title="No hay suscripciones para mostrar." />
        ) : (
          <div className="table__wrap">
            <table className="table table--stack">
              <thead>
                <tr>
                  <th>Evento</th>
                  <th>Destino</th>
                  <th>Autenticación</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id}>
                    <td className="table__lead mono">{s.eventType}</td>
                    <td data-label="Destino">
                      {s.system}
                      <br />
                      <span className="muted">{s.targetUrl}</span>
                    </td>
                    <td data-label="Autenticación">
                      {s.authentication === 'api_key' ? 'API key' : 'HMAC'} ·{' '}
                      {s.hasSecret ? 'Configurada' : 'Sin secreto'}
                    </td>
                    <td data-label="Estado">
                      <span className={s.active ? 'badge badge--ok' : 'badge'}>
                        {s.active ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>
                      <div className="table__actions">
                        <button className="button button--sm" onClick={() => setHistory(s)}>
                          Entregas
                        </button>
                        {canWrite && (
                          <>
                            <button
                              className="button button--sm"
                              disabled={busy || editing !== undefined}
                              onClick={() => {
                                setEditing(s);
                                setNotice('');
                              }}
                            >
                              Editar
                            </button>
                            <button
                              className="button button--sm"
                              disabled={busy || editing !== undefined}
                              onClick={() => toggle(s)}
                            >
                              {s.active ? 'Desactivar' : 'Activar'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {history && (
        <DeliveryHistory key={history.id} subscription={history} onClose={() => setHistory(null)} />
      )}
    </div>
  );
}
